import { useCallback } from "react";
import { Toast } from "primereact/toast";

import {
  logFollowUpCall,
  resolveMoreInfo as resolveMoreInfoApi,
  sendNextReminder,
  snoozeReminders,
} from "../services/onboarding/reminders";
import { reminderErrorMessage } from "../pages/Onboarding/Prospects/components/reminderErrors";

// The dialogs show a refusal inline, next to the action that was refused, so a
// failure comes back as words rather than as a toast.
// `message` is set exactly when `ok` is false.
export interface ReminderActionResult {
  ok: boolean;
  message?: string;
}

interface UseProspectRemindersParams {
  id: string;
  toastRef: React.RefObject<Toast>;
  // The prospect detail's reload: the ladder arrives on GET /admin/prospects/{id},
  // and a call or a resolved request also changes the audit and the flags there.
  reload: () => Promise<unknown>;
}

export interface UseProspectReminders {
  snooze: (until: string | null) => Promise<ReminderActionResult>;
  sendNext: () => Promise<ReminderActionResult>;
  logCall: (note: string, snoozeUntil?: string | null) => Promise<ReminderActionResult>;
  resolveMoreInfo: () => Promise<ReminderActionResult>;
}

/**
 * The ops actions on a prospect's onboarding reminders (CWA-189). No fetch of
 * its own: the ladder is part of the prospect detail, which is reloaded after
 * each action so the reminders, the flags and the edit history agree.
 */
const useProspectReminders = ({
  id,
  toastRef,
  reload,
}: UseProspectRemindersParams): UseProspectReminders => {
  const act = useCallback(
    async (action: () => Promise<unknown>, successMessage: string): Promise<ReminderActionResult> => {
      try {
        await action();
      } catch (e) {
        console.error("Reminder action failed", e);
        return { ok: false, message: reminderErrorMessage(e) };
      }
      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: successMessage,
        life: 3000,
      });
      await reload();
      return { ok: true };
    },
    [reload, toastRef]
  );

  const snooze = useCallback(
    (until: string | null) =>
      act(() => snoozeReminders(id, until), until ? "Reminders snoozed." : "Snooze removed."),
    [act, id]
  );

  const sendNext = useCallback(() => act(() => sendNextReminder(id), "Reminder sent."), [act, id]);

  const logCall = useCallback(
    (note: string, snoozeUntil?: string | null) =>
      act(() => logFollowUpCall(id, note, snoozeUntil), "Call logged."),
    [act, id]
  );

  const resolveMoreInfo = useCallback(
    () => act(() => resolveMoreInfoApi(id), "Marked as received."),
    [act, id]
  );

  return { snooze, sendNext, logCall, resolveMoreInfo };
};

export default useProspectReminders;
